import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { IconPanelRightOpen } from "@/assets/icons/geist/IconPanelRightOpen";
import { withGitpodSize } from "@/assets/icons/utils";
import { AgentConfigMenuItems } from "@/components/ai/AgentConfigMenuItems";
import { AgentExecutionDropdownMenu } from "@/components/ai/AgentExecutionDropdownMenu";
import {
    ConversationContent,
    ConversationError,
    ConversationInput,
} from "@/components/ai/chat/AgentExecutionConversation";
import { PendingInteractiveBlock } from "@/components/ai/PendingInteractiveBlock";
import { ConvertToProjectBanner } from "@/components/environments/ConvertToProjectBanner";
import { Button } from "@/components/flexkit/Button";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useDesktop } from "@/hooks/use-desktop";
import { useDetailsPanelToggleShortcut } from "@/hooks/use-details-panel-toggle-shortcut";
import { useFlagAcpAgentsEnabled, useFlagDevRunnerRegistrationEnabled } from "@/hooks/use-flag";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useConversationState } from "@/hooks/use-conversation-state";
import { useBuildButtonNotification } from "@/hooks/use-build-button-notification";
import type { PlainAgentExecution } from "@/queries/agent-queries";

import { agentExecutionName } from "@/utils/agent-executions";
import { ToggleDetailsPanelKeyBinding } from "@/utils/platform";
import { IconSidebarSimpleRightSquare } from "central-icons/IconSidebarSimpleRightSquare";
import { useMemo, type FC } from "react";

const SidebarIconClosed = withGitpodSize(IconSidebarSimpleRightSquare);

export type EnvironmentAgentExecutionConversationProps = {
    agentExecution: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    environmentId?: string;
    onBuildButtonAppear?: (specFilePath: string | null) => void;
};

export const EnvironmentAgentExecutionConversation: FC<EnvironmentAgentExecutionConversationProps> = ({
    agentExecution,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
    environmentId,
    onBuildButtonAppear,
}) => {
    const {
        error,
        loadingMessage,
        messages,
        availableCommands,
        pendingInteractiveBlock,
        handleNewMessage,
        handleSendMessage,
    } = useConversationState(agentExecution, environmentId);

    useBuildButtonNotification(pendingInteractiveBlock, messages, onBuildButtonAppear);

    useDetailsPanelToggleShortcut(() => {
        onToggleDetailsPanel();
    });

    return (
        <div
            data-testid="environment-agent-execution-conversation"
            className={cn("flex h-full min-w-[100px] flex-col rounded-l-lg border-border-base bg-surface-01")}
        >
            <Header
                agentExecution={agentExecution}
                isDetailsPanelOpen={isDetailsPanelOpen}
                onToggleDetailsPanel={onToggleDetailsPanel}
                environmentId={environmentId}
            />
            {environmentId && <ConvertToProjectBanner environmentId={environmentId} />}

            <ConversationError error={error} loadingMessage={loadingMessage} />
            <ConversationContent
                messages={messages}
                agentExecution={agentExecution}
                environmentId={environmentId}
                onSendMessage={handleSendMessage}
                pendingInteractiveBlock={pendingInteractiveBlock}
            />
            <ConversationInput
                hasMessages={messages.length > 0}
                agentExecution={agentExecution}
                onNewMessage={(message) => handleNewMessage(message, messages)}
                messages={messages}
                environmentId={environmentId}
                availableCommands={availableCommands}
                pendingInteractiveBlock={
                    <PendingInteractiveBlock
                        fragment={pendingInteractiveBlock}
                        onSendMessage={handleSendMessage}
                        environmentId={environmentId}
                    />
                }
                pendingInteractiveBlockType={pendingInteractiveBlock?.type}
            />
        </div>
    );
};

const PreConversationDropdown: FC<{ environmentId?: string }> = ({ environmentId }) => {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                className="group flex min-w-0 cursor-pointer items-center focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-0 focus-visible:outline-content-primary"
                aria-label="Conversation settings"
            >
                <div
                    className={cn(
                        "flex min-w-0 items-center gap-1 rounded-l-md p-1 transition-colors group-hover:bg-surface-hover group-data-[state=open]:bg-surface-hover",
                    )}
                >
                    <span className="min-w-0 truncate text-left text-base font-bold">Session</span>
                </div>
                <div
                    className={cn(
                        "flex flex-shrink-0 items-center rounded-r-md px-1 py-1.5 transition-colors group-hover:bg-surface-hover group-data-[state=open]:bg-surface-hover",
                    )}
                >
                    <IconChevronDownSmall size="sm" className="size-4" />
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
                <AgentConfigMenuItems environmentId={environmentId} />
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const Header: FC<{
    agentExecution: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    environmentId?: string;
}> = ({ agentExecution, isDetailsPanelOpen, onToggleDetailsPanel, environmentId }) => {
    const displayName = useMemo(
        () => (agentExecution ? agentExecutionName(agentExecution) : "Session"),
        [agentExecution],
    );
    const { isDesktop } = useDesktop();
    const { isMobileViewport: isMobile } = useIsMobileViewport();
    const { value: isAcpEnabled } = useFlagAcpAgentsEnabled();
    const { value: isDevRunnerEnabled } = useFlagDevRunnerRegistrationEnabled();
    const showConfigDropdown = isAcpEnabled || isDevRunnerEnabled;

    return (
        <div
            className={cn(
                "flex h-12 w-full items-center justify-between gap-2 border-b border-border-subtle px-4 py-2",
            )}
        >
            <div className="flex h-6 min-w-0 flex-1 items-center gap-0.5">
                {agentExecution ? (
                    <AgentExecutionDropdownMenu agentExecution={agentExecution} />
                ) : showConfigDropdown ? (
                    <PreConversationDropdown environmentId={environmentId} />
                ) : (
                    <div className="flex min-w-0 items-center gap-1 rounded-md p-1">
                        <span className="min-w-0 truncate text-left text-base font-bold">{displayName}</span>
                    </div>
                )}
            </div>
            {/* On desktop the DesktopTitlebar already provides this toggle */}
            {!isDetailsPanelOpen && !isDesktop && (
                <div className="flex items-center justify-end gap-1">
                    {isMobile ? (
                        <Tooltip content="Show environment details">
                            <Button
                                variant="ghost"
                                className="rounded-[4px] border-0 p-0"
                                size="lg"
                                onClick={onToggleDetailsPanel}
                                LeadingIcon={IconPanelRightOpen}
                                aria-label="Show environment details"
                                data-tracking-id="show-environment-details-agent-execution-conversation"
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip
                            content={
                                <div className="flex flex-row items-center gap-2">
                                    <span>Show details panel</span>
                                    <KeyboardCombo keys={ToggleDetailsPanelKeyBinding} />
                                </div>
                            }
                        >
                            <Button
                                variant="ghost"
                                className="rounded-[4px] border-0 p-2"
                                size="sm"
                                onClick={onToggleDetailsPanel}
                                aria-label="Show right panel"
                                data-tracking-id="toggle-details-panel-agent-execution-conversation"
                            >
                                <SidebarIconClosed size="sm" />
                            </Button>
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
};
